import { useCallback, useEffect, useRef, useState } from 'react'
import { logger } from '$utils'

/** How long the transient `copied` flag stays true after a successful copy. */
const COPIED_FEEDBACK_DURATION_MS = 1000

export interface UseCopyToClipboardResult {
  /** True for a short window after a successful copy, for transient UI feedback. */
  copied: boolean
  /**
   * Writes the given text to the clipboard and, on success, flips `copied`
   * true for {@link COPIED_FEEDBACK_DURATION_MS}.
   * @returns true when the write succeeded, false otherwise
   */
  copy: (text: string) => Promise<boolean>
}

/**
 * Copies text to the clipboard and exposes a transient `copied` flag for
 * "Copied" feedback (tooltip / icon swap). The flag auto-resets and is cleared
 * on unmount so feedback never lingers across renders.
 *
 * @param feedbackDurationMs how long `copied` stays true after a copy
 */
export function useCopyToClipboard(
  feedbackDurationMs: number = COPIED_FEEDBACK_DURATION_MS
): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false)
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Clear any pending reset timer on unmount so we never call setState after
  // the component has gone away.
  useEffect(
    () => () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
    },
    []
  )

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text)

        setCopied(true)

        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current)
        }
        resetTimeoutRef.current = setTimeout(
          () => setCopied(false),
          feedbackDurationMs
        )

        return true
      } catch (error) {
        // A clipboard write can reject (denied permission, insecure context).
        // We swallow it here and report failure so callers can decide what to do.
        logger.error('Failed to copy text to clipboard: %s', error)
        return false
      }
    },
    [feedbackDurationMs]
  )

  return { copied, copy }
}
